import { graphql, HttpResponse } from 'msw'
import {
  AchAccountType,
  type BankAccountActivityOutput,
  type BankAccountDetail,
  BankAccountStatus,
  BankAccountTitle,
  BankAccountType,
  BankActivityTypes,
  type CreatePlaidLinkTokenV2Error,
  CreatePlaidLinkTokenV2ErrorType,
  EgmCardStatus,
  type LinkAchAccountError,
  LinkAchAccountErrorType,
  type LinkAchAccountSuccess,
  LinkedAccountStatus,
  type LinkPlaidAchAccountError,
  type PatchAchAccountError,
  PatchAchAccountErrorType,
  PlaidLinkAchErrorType,
  type PlaidLinkToken,
  TransactionMethod,
  type UnlinkAchAccountOutput,
  type UpdateBankAccountName,
  type UpdateBankAccountNameError,
  UpdateBankAccountNameErrorType,
  type UpdateProfileError,
  UpdateProfileErrorType,
  UpdateProfileStatus,
  type UpdateProfileSuccess,
} from '../../models'

export const bankAccountDetailMock: BankAccountDetail = {
  accountTitle: BankAccountTitle.SINGLE,
  accountType: BankAccountType.HIGH_YIELD_CHECKING,
  alias: 0,
  apy: 4.0,
  status: BankAccountStatus.OPEN,
  accountName: 'High Yield Checking Account',
  routingNumber: '125109019',
  accountNumber: '314896161',
  debitCard: [
    {
      cardId: 'v-402-48403565-3606-43f4-9cbd-35d5a71952f9',
      cardName: 'Evergreen Debit Card',
      maskedPAN: '************8535',
      cardStatus: EgmCardStatus.PENDING_ACTIVATION,
      expDate: '2029-10-01',
      needToSetPin: true,
      orderDetails: [
        {
          cardId: 'v-402-48403565-3606-43f4-9cbd-35d5a71952f9',
          cardStockId: 'v-402-48403565-3606-43f4-9cbd-35d5a71952f9',
          dateTime: '2021-10-01T00:00:00Z',
          embossedLine1: 'John Doe',
          orderReason: 'New Card',
          primaryAccountNumber: '************8535',
          shippingMethod: 'USPS',
          collateralId: 'v-402-48403565-3606-43f4-9cbd-35d5a71952f9',
          embossedLine2: 'Evergreen',
          expirationDate: '2029-10-01',
          formFactor: 'EMV',
          orderId: 'v-402-48403565-3606-43f4-9cbd-35d5a71952f9',
        },
      ],
    },
  ],
  balance: {
    total: {
      amount: 0.0,
      currencyCode: 'USD',
    },
    available: {
      amount: 0.0,
      currencyCode: 'USD',
    },
  },
  achLimits: {
    __typename: 'AchLimits',
    credit: {
      singleTransactionLimit: 100000.0,
      dailyLimit: 100000.0,
      monthlyLimit: 250000.0,
    },
    debit: {
      singleTransactionLimit: 100000.0,
      dailyLimit: 100000.0,
      monthlyLimit: 250000.0,
    },
  },
  attributes: {
    minimumDeposit: 25000,
    minimumDepositMet: true,
  },
}

export const bankActivityMock: BankAccountActivityOutput = {
  activity: [
    {
      activityId: '5-djnf7_JnhdgEqGxn2F27hX',
      displayName: 'Transfer to Personal Portfolio',
      date: '2024-12-03T17:49:48.080Z',
      amount: {
        currencyCode: 'USD',
        amount: -250.0,
      },
      isProcessing: false,
      method: TransactionMethod.INVESTMENTS,
      label: 'Transfer to Personal Portfolio',
      note: 'Transfer to Brokerage Account',
      __typename: BankActivityTypes.OtherBankActivity,
    },
    {
      activityId: '5-nosLzhSW-AciKrTiFLnp2R',
      displayName: 'Interest Earned',
      date: '2024-12-01T04:59:59.000Z',
      amount: {
        currencyCode: 'USD',
        amount: 0.1,
      },
      isProcessing: false,
      method: TransactionMethod.ADMINISTRATIVE,
      label: 'Interest Earned',
      note: 'Interest Earned',
      __typename: BankActivityTypes.OtherBankActivity,
    },
    {
      activityId: '5-jSfehx2HNeFO4f5jbFSNlG',
      displayName: 'Deposit from JP Morgan Chase Bank',
      date: '2024-11-27T15:43:43.925Z',
      amount: {
        currencyCode: 'USD',
        amount: 250.0,
      },
      isProcessing: false,
      method: TransactionMethod.ACH,
      label: 'Deposit',
      note: 'ACH Transfer In',
      __typename: BankActivityTypes.AchBankActivity,
    },
  ],
}

export const updateBankAccountNameMock: UpdateBankAccountName = {
  __typename: 'UpdateBankAccountName',
  isSuccessful: true,
}

export const updateCustomerAddress: UpdateProfileSuccess = {
  __typename: 'UpdateProfileSuccess',
  status: UpdateProfileStatus.COMPLETE,
}

export const updateProfileSuccessMock: UpdateProfileSuccess = {
  __typename: 'UpdateProfileSuccess',
  status: UpdateProfileStatus.COMPLETE,
}

export const updateProfileErrorMock: UpdateProfileError = {
  __typename: 'UpdateProfileError',
  type: UpdateProfileErrorType.VALIDATION_FAILED,
}

export const updateBankAccountNameErrorMock: UpdateBankAccountNameError = {
  __typename: 'UpdateBankAccountNameError',
  errorType: UpdateBankAccountNameErrorType.FAILED_TO_UPDATE_ACCOUNT_NAME,
}

export const mockGetBankAccountDetailSuccessHandler = graphql.query('bankAccountDetail', () => {
  return HttpResponse.json({ data: { bankAccountDetail: bankAccountDetailMock } })
})

export const mockGetBankActivityHandler = graphql.query('bankAccountActivity', () => {
  return HttpResponse.json({ data: { bankAccountActivity: bankActivityMock } })
})

const mockGetBankAccountDetailHandler = mockGetBankAccountDetailSuccessHandler

export const mockUpdateBankAccountNameSuccess = graphql.mutation('updateBankAccountName', () => {
  return HttpResponse.json({ data: { updateBankAccountName: updateBankAccountNameMock } })
})

export const mockUpdateBankAccountNameError = graphql.mutation('updateBankAccountName', () => {
  return HttpResponse.json({ data: { updateBankAccountName: updateBankAccountNameErrorMock } })
})

const mockUpdateBankAccountName = mockUpdateBankAccountNameSuccess

/* Customer Address Update */
export const mockUpdateCustomerAddressSuccess = graphql.mutation('updateAddress', () => {
  return HttpResponse.json({ data: { updateAddress: updateCustomerAddress } })
})

const mockUpdateCustomerAddress = mockUpdateCustomerAddressSuccess

/* Employment Info Update */
export const mockUpdateEmploymentInfoSuccessHandler = graphql.mutation('updateEmploymentInfo', () => {
  return HttpResponse.json({ data: { updateEmploymentInfo: updateProfileSuccessMock } })
})

export const mockUpdateEmploymentInfoErrorHandler = graphql.mutation('updateEmploymentInfo', () => {
  return HttpResponse.json({ data: { updateEmploymentInfo: updateProfileErrorMock } })
})

const mockUpdateEmploymentInfo = mockUpdateEmploymentInfoSuccessHandler

/* Financial Info Update */
export const mockUpdateFinancialInfoSuccessHandler = graphql.mutation('updateEmploymentInfo', () => {
  return HttpResponse.json({ data: { updateFinancialInfo: updateProfileSuccessMock } })
})

export const mockUpdateFinancialInfoErrorHandler = graphql.mutation('updateFinancialInfo', () => {
  return HttpResponse.json({ data: { updateFinancialInfo: updateProfileErrorMock } })
})

const mockUpdateFinancialInfo = mockUpdateFinancialInfoSuccessHandler

/* Suitability Info Update */
export const mockUpdateSuitabilitySuccessHandler = graphql.mutation('updateSuitability', () => {
  return HttpResponse.json({ data: { updateSuitability: updateProfileSuccessMock } })
})

export const mockUpdateSuitabilityErrorHandler = graphql.mutation('updateFinancialInfo', () => {
  return HttpResponse.json({ data: { updateFinancialInfo: updateProfileErrorMock } })
})

const mockUpdateSuitability = mockUpdateSuitabilitySuccessHandler

export const linkedAchAccountMock = {
  __typename: 'linkedAchAccount',
  alias: 0,
  name: 'Chase Checking',
  mask: 'x-1234',
  achType: AchAccountType.CHECKING,
  linkedAccountStatus: LinkedAccountStatus.ACTIVE,
  institutionName: 'JP Morgan Chase Bank',
  identifier: 'asas-23rf-fdsf-1234',
}

export const editLinkedAchAccountNameErrorMock: PatchAchAccountError = {
  __typename: 'PatchAchAccountError',
  errorType: PatchAchAccountErrorType.ACCOUNT_NOT_FOUND,
}

export const mockEditLinkedAchAccountNameSuccess = graphql.mutation('editLinkedAchAccountName', () => {
  return HttpResponse.json({ data: { editLinkedAchAccountName: linkedAchAccountMock } })
})

export const mockEditLinkedAchAccountNameError = graphql.mutation('editLinkedAchAccountName', () => {
  return HttpResponse.json({ data: { editLinkedAchAccountName: editLinkedAchAccountNameErrorMock } })
})

const mockEditLinkedAchAccountName = mockEditLinkedAchAccountNameSuccess

export const unlinkAchAccountMock: UnlinkAchAccountOutput = {
  isSuccessful: true,
}

export const mockUnlinkAchAccount = graphql.mutation('unlinkAchAccount', () => {
  return HttpResponse.json({ data: { unlinkAchAccount: unlinkAchAccountMock } })
})

export const plaidLinkTokenMock: PlaidLinkToken = {
  __typename: 'PlaidLinkToken',
  linkToken: 'link-sandbox-1234',
  expiration: '2024-12-31T23:59:59Z',
}

export const createPlaidLinkTokenV2Error: CreatePlaidLinkTokenV2Error = {
  __typename: 'CreatePlaidLinkTokenV2Error',
  errorType: CreatePlaidLinkTokenV2ErrorType.UNABLE_TO_CREATE_LINK_TOKEN,
}

export const mockCreatePlaidLinkTokenV2Success = graphql.query('createPlaidLinkTokenV2', () => {
  return HttpResponse.json({ data: { createPlaidLinkTokenV2: plaidLinkTokenMock } })
})

export const mockCreatePlaidLinkTokenV2Error = graphql.query('createPlaidLinkTokenV2', () => {
  return HttpResponse.json({ data: { createPlaidLinkTokenV2: createPlaidLinkTokenV2Error } })
})

export const mockCreatePlaidLinkTokenV2 = mockCreatePlaidLinkTokenV2Success

export const linkPlaidAchAccountErrorMock: LinkPlaidAchAccountError = {
  __typename: 'LinkPlaidAchAccountError',
  errorType: PlaidLinkAchErrorType.INVALID_PLAID_LINK_ACCOUNT_TYPE,
}

export const mockLinkPlaidAchAccountSuccess = graphql.mutation('linkPlaidAchAccount', () => {
  return HttpResponse.json({ data: { linkPlaidAchAccount: linkedAchAccountMock } })
})

export const mockLinkPlaidAchAccountError = graphql.mutation('linkPlaidAchAccount', () => {
  return HttpResponse.json({ data: { linkPlaidAchAccount: linkPlaidAchAccountErrorMock } })
})

export const mockLinkPlaidAchAccount = mockLinkPlaidAchAccountSuccess

export const linkAchAccountSuccessMock: LinkAchAccountSuccess = {
  __typename: 'LinkAchAccountSuccess',
  alias: 1,
  displayName: 'JP Morgan Chase Checking',
  mask: 'x-1234',
  linkedAccountStatus: LinkedAccountStatus.ACTIVE,
}

export const linkAchAccountErrorMock: LinkAchAccountError = {
  __typename: 'LinkAchAccountError',
  errorType: LinkAchAccountErrorType.UNABLE_TO_LOOKUP_ROUTING_NUMBER,
}

export const mockLinkAchAccountSuccess = graphql.mutation('linkAchAccount', () => {
  return HttpResponse.json({ data: { linkAchAccount: linkAchAccountSuccessMock } })
})

export const mockLinkAchAccountError = graphql.mutation('linkAchAccount', () => {
  return HttpResponse.json({ data: { linkAchAccount: linkAchAccountErrorMock } })
})

export const mockLinkAchAccount = mockLinkAchAccountSuccess

export const mockAccount = [
  mockGetBankAccountDetailHandler,
  mockGetBankActivityHandler,
  mockUpdateBankAccountName,
  mockUpdateCustomerAddress,
  mockUpdateEmploymentInfo,
  mockUpdateFinancialInfo,
  mockUpdateSuitability,
  mockEditLinkedAchAccountName,
  mockUnlinkAchAccount,
  mockCreatePlaidLinkTokenV2,
  mockLinkPlaidAchAccount,
  mockLinkAchAccount,
]
